/**
 * Achievements Page
 * Full page displaying all achievements for the gamer
 */

import React, { useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { useGetUserAchievements } from '../../../apiLayer/queryHooks/queries'
import { transformUserAchievementsToAchievements } from '../achievements/helpers/userAchievementsTransformer'
import { isAchievementCompleted } from '../achievements/helpers/achievementHelpers'
import { useFeatureFlags } from '../../../shared/hooks/useFeatureFlags'
import { useNavigate } from 'react-router-dom'
import { AchievementRow } from '../achievements/components/AchievementRow'
import { AchievementsPageHeader } from '../achievements/components/AchievementsPageHeader'
import { EmptyStateCard } from '../components/cards/EmptyStateCard'
import { PageWrapper } from '../../../shared/components'
import { Spinner } from '../../../interaxDesignSystemComponents'
import { TrophyIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS } from '../../../constants/colors'
import { GamerRoutes } from '../../../config/routes/routeEnums'
import { GamerFeedbackBackButton, GamerFeedbackContainerBox } from '../../feedbackGenerator/components'

type FilterType = 'all' | 'completed' | 'uncompleted'

/**
 * AchievementsPage - Full page showing all achievements
 */
export const AchievementsPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isAchievementsEnabled } = useFeatureFlags()
  const [filter, setFilter] = useState<FilterType>('completed')
  const { data: achievementsResponse, isLoading, error } = useGetUserAchievements()

  // Transform achievements data - must be before early returns
  const achievements = useMemo(() => {
    if (!achievementsResponse?.data) {
      return []
    }
    return transformUserAchievementsToAchievements(achievementsResponse.data)
  }, [achievementsResponse?.data])

  const filteredAchievements = useMemo(() => {
    switch (filter) {
      case 'completed':
        return achievements.filter((a) => isAchievementCompleted(a))
      case 'uncompleted':
        return achievements.filter((a) => !isAchievementCompleted(a))
      case 'all':
        return achievements
      default:
        // Default to showing only completed achievements
        return achievements.filter((a) => isAchievementCompleted(a))
    }
  }, [achievements, filter])

  const sortedAchievements = useMemo(() => {
    return [...filteredAchievements].sort((a, b) => {
      const aCompleted = isAchievementCompleted(a)
      const bCompleted = isAchievementCompleted(b)

      // Completed achievements first
      if (aCompleted !== bCompleted) {
        return aCompleted ? -1 : 1
      }

      // Then sort by value/level for tiered achievements
      if (a.type === 'tiered' && b.type === 'tiered') {
        return b.currentValue - a.currentValue
      }

      // Simple achievements by completion
      if (a.type === 'simple' && b.type === 'simple') {
        return a.isCompleted === b.isCompleted ? 0 : a.isCompleted ? -1 : 1
      }

      return 0
    })
  }, [filteredAchievements])

  // Redirect if feature flag is disabled
  React.useEffect(() => {
    if (!isAchievementsEnabled) {
      navigate('/gamer')
    }
  }, [isAchievementsEnabled, navigate])

  if (!isAchievementsEnabled) {
    return null
  }

  if (isLoading) {
    return (
      <PageWrapper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          backgroundColor: INTERAX_COLORS.offBlack
        }}
      >
        <GamerFeedbackBackButton onClick={() => navigate(GamerRoutes.MY_PROFILE)} />
        <GamerFeedbackContainerBox variant="team" sx={{ width: '100%', maxWidth: '1400px', mx: 'auto' }}>
          <AchievementsPageHeader />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px'
            }}
          >
            <Spinner size="large" color="sun" />
          </Box>
        </GamerFeedbackContainerBox>
      </PageWrapper>
    )
  }

  if (error) {
    return (
      <PageWrapper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          backgroundColor: INTERAX_COLORS.offBlack
        }}
      >
        <GamerFeedbackBackButton onClick={() => navigate(GamerRoutes.MY_PROFILE)} />
        <GamerFeedbackContainerBox variant="team" sx={{ width: '100%', maxWidth: '1400px', mx: 'auto' }}>
          <AchievementsPageHeader />
          <Box
            sx={{
              padding: 3,
              borderRadius: '10px',
              border: `1px solid ${INTERAX_COLORS.white04}`
            }}
          >
            <Box
              sx={{
                color: INTERAX_COLORS.redError,
                fontSize: '16px',
                textAlign: 'center'
              }}
            >
              {t('gamer.achievements.error.loadingFailed', 'Failed to load achievements')}
            </Box>
          </Box>
        </GamerFeedbackContainerBox>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        backgroundColor: INTERAX_COLORS.offBlack,
        position: 'relative'
      }}
    >
      {/* Back Button - Outside the container */}
      <GamerFeedbackBackButton onClick={() => navigate(GamerRoutes.MY_PROFILE)} />
      <GamerFeedbackContainerBox variant="team" sx={{ width: '100%', maxWidth: '1400px', mx: 'auto', mt: 0 }}>
        <AchievementsPageHeader />

        {/* Filter Buttons */}
        <Box sx={{ order: 0, width: '100%', flex: 'none', alignSelf: 'stretch' }}>
          {achievements.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 3,
                flexWrap: 'wrap'
              }}
            >
              {(['all', 'completed', 'uncompleted'] as FilterType[]).map((filterType) => (
                <Box
                  key={filterType}
                  component="button"
                  onClick={() => setFilter(filterType)}
                  sx={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: `1px solid ${filter === filterType ? INTERAX_COLORS.sun : INTERAX_COLORS.white04}`,
                    backgroundColor: filter === filterType ? INTERAX_COLORS.sun40 : 'transparent',
                    color: filter === filterType ? INTERAX_COLORS.white : INTERAX_COLORS.typographyFont,
                    fontFamily: 'Lexend Deca, sans-serif',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: filter === filterType ? INTERAX_COLORS.sun40 : INTERAX_COLORS.white04,
                      borderColor: INTERAX_COLORS.sun
                    }
                  }}
                >
                  {t(
                    `gamer.achievements.filter.${filterType}`,
                    filterType.charAt(0).toUpperCase() + filterType.slice(1)
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
        {/* Achievements Grid - 2 columns on large screens */}
        {sortedAchievements.length === 0 ? (
          <EmptyStateCard
            icon={<TrophyIcon style={{ width: 64, height: 64, color: INTERAX_COLORS.heritageBlue }} />}
            message={
              filter === 'all'
                ? t('gamer.achievements.emptyState.message', 'No achievements yet. Keep playing to unlock them!')
                : t('gamer.achievements.emptyState.filtered', {
                    filter: t(`gamer.achievements.filter.${filter}`, filter.charAt(0).toUpperCase() + filter.slice(1))
                  })
            }
          />
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr', md: 'repeat(2, 1fr)' },
              gap: 2
            }}
          >
            {sortedAchievements.map((achievement) => (
              <AchievementRow key={achievement.id} achievement={achievement} />
            ))}
          </Box>
        )}
      </GamerFeedbackContainerBox>
    </PageWrapper>
  )
}
